import type { LiveCall } from './types';

export const MOCK_CALLS: LiveCall[] = [
  {
    id: 'c1',
    direction: 'inbound',
    num: '01',
    name: 'Marcia Hollis',
    phone: '(415) 555-0142',
    isReturning: true,
    location: 'San Francisco, CA',
    duration: 145,
    sentiment: 'positive',
    speaker: 'elva',
    phase: 'resolve',
    utterance:
      'Tuesday at 2:30 pm with Dr. Lin works perfectly. Is that still good for you?',
    summary:
      "Returning patient calling to book a routine 6-month cleaning. Insurance (Delta Dental PPO) confirmed on file. Elva looked up hygienist availability and offered Tuesday 2:30 pm with Dr. Lin — now awaiting the patient's confirmation.",
    goal: 'Book cleaning',
    status: 'Offered Tue 2:30 pm with Dr. Lin',
    actions: [
      { t: 4, icon: 'phone', label: 'Call started', sub: 'Inbound · (415) 555-0142', state: 'done' },
      { t: 9, icon: 'sparkle', label: 'Greeted patient', sub: 'Hi, this is Elva at Bayside Dental…', state: 'done' },
      { t: 22, icon: 'bolt', label: 'Intent: book a cleaning', sub: 'Routine 6-month checkup', state: 'done' },
      {
        t: 41,
        icon: 'clipboard',
        label: 'Confirmed insurance on file',
        sub: 'Delta Dental PPO · last verified Mar 12',
        state: 'done',
      },
      {
        t: 88,
        icon: 'search',
        label: 'Looking up hygienist availability',
        sub: 'Tue–Thu next week',
        state: 'done',
      },
      {
        t: 118,
        icon: 'chat',
        label: 'Offered Tue 2:30 pm with Dr. Lin',
        sub: 'Awaiting confirmation',
        state: 'running',
      },
    ],
    facts: [
      { k: 'Reason', v: 'Routine cleaning' },
      { k: 'Insurance', v: 'Delta Dental PPO' },
      { k: 'Last visit', v: 'Oct 14, 2025' },
      { k: 'Provider pref.', v: 'Dr. Lin' },
    ],
  },
  {
    id: 'c2',
    direction: 'outbound-insurance',
    num: '02',
    name: 'Verifying — A. Okonkwo',
    phone: 'Cigna · 800-244-6224',
    isReturning: false,
    location: 'Cigna IVR',
    duration: 88,
    sentiment: 'neutral',
    speaker: 'patient',
    phase: 'verify',
    utterance:
      'Press 2 for claims and eligibility. Please hold for the next available representative.',
    summary:
      'Outbound eligibility check with Cigna on behalf of patient A. Okonkwo. Elva navigated the IVR to claims & eligibility and read back the patient identifiers; currently pulling the 2026 plan-year benefit summary.',
    goal: 'Eligibility check',
    status: 'Pulling benefit summary',
    actions: [
      { t: 2, icon: 'phone', label: 'Outbound to Cigna', sub: 'Eligibility check', state: 'done' },
      { t: 14, icon: 'dots', label: 'Navigating IVR', sub: 'Tier 2 → claims & eligibility', state: 'done' },
      {
        t: 48,
        icon: 'clipboard',
        label: 'Read patient identifiers',
        sub: 'DOB · member ID · group',
        state: 'done',
      },
      { t: 64, icon: 'search', label: 'Pulling benefit summary', sub: 'Plan year 2026', state: 'running' },
    ],
  },
  {
    id: 'c3',
    direction: 'inbound',
    num: '03',
    name: 'Carlos Reyes',
    phone: '(510) 555-0917',
    isReturning: false,
    location: 'Oakland, CA',
    duration: 59,
    sentiment: 'frustrated',
    speaker: 'patient',
    phase: 'intent',
    utterance:
      "I've had this throbbing pain since yesterday and now my whole jaw is swollen — I need to be seen today.",
    summary:
      'New caller reporting throbbing tooth pain since yesterday with visible jaw swelling, asking to be seen today. Flagged urgent — Elva is capturing intent and severity before triaging for a same-day emergency slot.',
    goal: 'Same-day emergency',
    status: 'Intent: tooth pain, urgent',
    urgent: true,
    actions: [
      { t: 3, icon: 'phone', label: 'Call started', sub: 'New caller', state: 'done' },
      { t: 8, icon: 'sparkle', label: 'Greeted patient', sub: 'Hi, this is Elva…', state: 'done' },
      {
        t: 28,
        icon: 'bolt',
        label: 'Intent: tooth pain, urgent',
        sub: 'Severe, started yesterday',
        state: 'running',
      },
    ],
  },
  {
    id: 'c4',
    direction: 'outbound-patient',
    num: '04',
    name: 'Reminder — J. Park',
    phone: '(650) 555-0291',
    isReturning: true,
    location: 'Palo Alto, CA',
    duration: 40,
    sentiment: 'neutral',
    speaker: 'elva',
    phase: 'confirm',
    utterance:
      'Just confirming your cleaning Thursday at 10 a.m. with Dr. Hwang. Reply yes to confirm.',
    summary:
      'Outbound reminder to returning patient J. Park for a cleaning Thursday 10:00 am with Dr. Hwang. Elva is confirming attendance and waiting on a yes-to-confirm reply.',
    goal: 'Appointment reminder',
    status: 'Confirming Thu 10:00 am',
    actions: [
      { t: 1, icon: 'phone', label: 'Outbound to patient', sub: 'Appointment reminder', state: 'done' },
      {
        t: 14,
        icon: 'chat',
        label: 'Confirming Thu 10:00 am',
        sub: 'Cleaning · Dr. Hwang',
        state: 'running',
      },
    ],
  },
  {
    id: 'c5',
    direction: 'inbound',
    num: '05',
    name: 'Eleanor Whitfield',
    phone: '(925) 555-0048',
    isReturning: true,
    location: 'Walnut Creek, CA',
    duration: 232,
    sentiment: 'positive',
    speaker: 'elva',
    phase: 'confirm',
    utterance:
      "All set — you're rebooked for Wednesday the 15th at 2 p.m. with Dr. Lin. Anything else?",
    summary:
      'Returning patient rescheduling an existing visit. Elva cancelled the Monday 9:00 am slot and rebooked Wednesday the 15th at 2:00 pm with Dr. Lin; now checking whether anything else is needed before wrapping up.',
    goal: 'Reschedule',
    status: 'Rebooked Wed 2:00 pm',
    actions: [
      { t: 6, icon: 'phone', label: 'Call started', sub: 'Returning patient', state: 'done' },
      { t: 19, icon: 'bolt', label: 'Intent: reschedule', sub: 'Existing appt next Mon', state: 'done' },
      {
        t: 84,
        icon: 'calendar',
        label: 'Cancelled Mon 9:00 am',
        sub: 'Booked Wed 2:00 pm',
        state: 'running',
      },
    ],
  },
  {
    id: 'c6',
    direction: 'inbound',
    num: '06',
    name: 'Unknown caller',
    phone: '(212) 555-0703',
    isReturning: false,
    location: 'New York, NY',
    duration: 35,
    sentiment: 'neutral',
    speaker: 'patient',
    phase: 'greet',
    utterance: "Hi — yeah, I'm calling because I think my crown came loose this morning…",
    summary:
      'New caller reporting a crown that came loose this morning. Elva has greeted the caller and is gathering the reason for the visit to triage the right next step.',
    goal: 'Triage',
    status: 'Greeting · gathering reason',
    actions: [
      { t: 2, icon: 'phone', label: 'Call started', sub: 'New caller', state: 'done' },
      { t: 9, icon: 'sparkle', label: 'Greeted patient', sub: 'Hi, this is Elva…', state: 'running' },
    ],
  },
  {
    id: 'c7',
    direction: 'outbound-insurance',
    num: '07',
    name: 'Verifying — D. Hagen',
    phone: 'Aetna · 800-872-3862',
    isReturning: false,
    location: 'Aetna IVR',
    duration: 176,
    sentiment: 'neutral',
    speaker: 'patient',
    phase: 'verify',
    utterance: 'You are caller number 4. Average wait time is approximately 6 minutes.',
    summary:
      'Outbound pre-authorization follow-up with Aetna for patient D. Hagen. Elva is holding in the claims queue at position 4, with an estimated 6-minute wait.',
    goal: 'Pre-auth follow-up',
    status: 'On hold · queue position 4',
    onHold: true,
    actions: [
      { t: 4, icon: 'phone', label: 'Outbound to Aetna', sub: 'Pre-auth follow-up', state: 'done' },
      { t: 26, icon: 'dots', label: 'On hold', sub: 'Queue position 4', state: 'running' },
    ],
  },
];
